import IndexStore from 'src/stores/index';
import type {Question, SurveyError} from 'src/types/survey';
import type {ModuleSequenceNode} from 'src/stores/survey';

import type {Responder} from 'src/stores/responder';
// @ts-expect-error - TS2614 - Module '"src/api-parsers/events"' has no exported member 'Event'. Did you mean to use 'import Event from "src/api-parsers/events"' instead? | TS2614 - Module '"src/api-parsers/events"' has no exported member 'SentEvent'. Did you mean to use 'import SentEvent from "src/api-parsers/events"' instead? | TS2614 - Module '"src/api-parsers/events"' has no exported member 'SurveyResponse'. Did you mean to use 'import SurveyResponse from "src/api-parsers/events"' instead?
import type {Event, SentEvent, SurveyResponse} from 'src/api-parsers/events';

import * as React from 'react';
import {connect} from 'react-redux';
import logger from 'src/utils/logger';
import {withFlux} from 'src/flux/component';
import flow from 'lodash/flow';
import partial from 'lodash/partial';
import find from 'lodash/find';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import debounce from 'lodash/debounce';

import * as actions from 'src/actions/survey';

import Loading from 'src/components/lib/loading/loading';
import HeadSetter from 'src/components/lib/head-setter';
import {Dialog} from 'src/components/lib/dialog';

import {selectSpamBotPreventionWithDoubleClick} from 'src/hooks/product-flags';
import DonePage from './modules/done';
import ExpiredPage from './modules/expired';
import DumbSurvey from './dumb';
import {BotDialog} from './bot-dialog';

import colors from 'src/styles/modules/colors.css';
import dsCss from 'designSystem2021';


type InitialResponse = {
  module_value: string;
  module_id: string;
};
const getInitialResponse = (query: {
  [key: string]: string;
  // @ts-expect-error - TS7030 - Not all code paths return a value.
}): InitialResponse | undefined => {
  const {module_value, module_id} = query;

  // See https://github.com/Spaced-Out/sense/issues/12865
  // at some point sendgrid started escpaing urls so `&` => `&amp;` so we might
  // have to check for a weird module_value like this:
  const possibleModuleValue = query['amp;module_value'] || module_value;

  if (possibleModuleValue) {
    return {
      module_value: possibleModuleValue,
      module_id,
    };
  }
};

const respondToFirstModule = async (
  store: IndexStore,
  sentEventId: string,
  response: SurveyResponse,
  initialResponse: InitialResponse,
) => {
  // Make sure we skip any Message modules that are in front of the actual first question in the survey
  // @ts-expect-error - TS2322 - Type 'Question | null | undefined' is not assignable to type 'Question'.
  const firstSurveyQuestion: Question = store.survey.firstQuestion(
    response.eventId,
  );

  // Module response value can be a number (for rating scale questions) or string (for multiple choice questions)
  const module_value = isNumber(initialResponse.module_value)
    ? parseInt(initialResponse.module_value, 10)
    : initialResponse.module_value;

  await actions.respond(
    store,
    response.id,
    sentEventId,
    initialResponse.module_id,
    firstSurveyQuestion,
    module_value,
    true,
  );
};
const proceedAfterFirstModule = async (
  store: IndexStore,
  sentEventId: string,
  response: SurveyResponse,
  onComplete: () => unknown,
) => {
  // Make sure we skip any Message modules that are in front of the actual first question in the survey
  // @ts-expect-error - TS2322 - Type 'Question | null | undefined' is not assignable to type 'Question'.
  const firstSurveyQuestion: Question = store.survey.firstQuestion(
    response.eventId,
  );
  const eventId = store.sentEvents.byId(sentEventId).eventId;
  const pos = store.survey.positionInSurvey(eventId, firstSurveyQuestion.id);
  const moduleSequence = actions.getModuleSequence(
    store,
    store.survey.byId(eventId),
  );

  // Advance the survey to the question/module after the one we just answered by clicking in the email
  if (pos != null) {
    if (pos === moduleSequence.length - 1) {
      // if the initial response is also the last one, mark the survey as finished
      await actions.finish(store, eventId, sentEventId);
      onComplete();
    }
    actions.goto(store, eventId, pos + 1);
  }
};

// @ts-expect-error - TS7031 - Binding element 'store' implicitly has an 'any' type.
const mapper = ({store, ...props}) => {
  const sentEventId = props.params.sentEventId;
  const sentEvent = store.sentEvents.byId(sentEventId);
  const {eventId} = sentEvent;

  const nextProps = {
    survey: store.survey.byId(eventId),
    progress: store.survey.progressById(eventId),
    sentEvent,
    responder: store.responders.byId(sentEvent.audienceMemberId),
    // this currently is actually more like a top level object that HOLDS
    // the current responses in the responseModules property.
    // TODO refactor this to more accurately reflect the object this
    // variable refers to
    currentResponse: store.surveyResponses.byEventId(eventId),
    responseStore: store.surveyResponses.state,
    currentError: store.surveyResponses.getCurrentError(),
    moduleSequence: store.survey.moduleSequenceById(eventId),
    isPossibleBot: store.survey.getIsPossibleBot(),
    spamBotPreventionWithDoubleClick: selectSpamBotPreventionWithDoubleClick(
      store.reduxStore.getState(),
    ),
  } as const;

  return {
    ...nextProps,
    currentQuestion: getCurrentQuestion(store, nextProps),
  };
};

type SurveyPageLoaderProps = {
  agencyDisplayName: string | null | undefined;
  agencyFavicon: string;
  ogImage: string;
  questionText: string;
};

const SurveyPageLoader = (props: SurveyPageLoaderProps) => {
  const {agencyDisplayName, agencyFavicon, ogImage, questionText} = props;
  return (
    <>
      <HeadSetter
        title={agencyDisplayName}
        faviconUrl={agencyFavicon}
        ogImageUrl={ogImage}
        ogDescription={questionText}
      />
      <Loading />
    </>
  );
};

class Survey extends React.Component<{
  store: IndexStore;
  survey: Event;
  progress: number;
  responder: Responder;
  sentEvent: SentEvent;
  currentResponse: SurveyResponse;
  currentError: SurveyError | null | undefined;
  params: Record<any, any>;
  moduleSequence: ModuleSequenceNode[];
  agencyDisplayName: string | null | undefined;
  multiEntityEnabled: boolean;
  currentQuestion?: Question;
  isPossibleBot: boolean;
  spamBotPreventionWithDoubleClick: boolean;
}> {
  respondAndAdvance: (value: any) => void;

  constructor(props: any) {
    super(props);
    this.track();

    // This is a pretty hefty debounce! Maybe we can revisit this, but it
    // was definitely possible to trigger this twice for a single question's response
    // with quick double-click advancing past the subsequent question and never
    // recording its response.
    this.respondAndAdvance = debounce(this._respondAndAdvance, 500, {
      leading: true,
      trailing: false,
    });
  }

  hasCompletedSurvey: boolean = false;

  // @ts-expect-error - TS4114 - This member must have an 'override' modifier because it overrides a member in the base class 'Component<{ store: IndexStore; survey: Event; progress: number; responder: AudienceMember; sentEvent: SentEvent; currentResponse: SurveyResponse; currentError: SurveyError | null | undefined; params: Record<any, any>; ... 5 more ...; spamBotPreventionWithDoubleClick: boolean; }, {}, any>'.
  async componentDidMount() {
    const {
      store,
      params: {sentEventId},
    } = this.props;

    //check for is_bot flag before doing anything
    await actions.checkForBot(store, sentEventId);
    await this.start(store, sentEventId);
  }

  async start(store: any, sentEventId: any) {
    const query = Object.fromEntries(
      new URLSearchParams(window.location.search).entries(),
    );
    const sentEvent = store.sentEvents.byId(sentEventId);

    logger.log(
      `got survey handshake token ${sentEvent.responseHandshakeToken} for survey ${sentEventId}`,
    );

    if (sentEvent.timeResponseHandshake == null) {
      logger.log('starting handshake');
      await actions.addSurveyResponseHandshake(
        store,
        sentEventId,
        sentEvent.responseHandshakeToken,
      );
    } else {
      logger.log(`last survey handshake at ${sentEvent.timeResponseHandshake}`);
    }

    // Can safely respond to survey now that handshake has finished
    // or previously... shaken?
    const initialResponse = getInitialResponse(query);

    let response;
    if (this.props.spamBotPreventionWithDoubleClick) {
      if (!query.module_value || !query.module_id) {
        try {
          await actions.setResponse(store, sentEventId); // Setting event response when query is null in case of sms survey message to auto populate db value
        } catch (err: any) {
          if (err.status === 404) {
            logger.info(`[info]: response not found for ${sentEventId}`, err);
          } else {
            logger.error(
              `[error]: uncaught error while setting response ${err}`,
            );
          }
        }
      }
      response = await actions.createResponseFromEmailAndSaveInBackup(
        store,
        sentEventId,
        initialResponse,
      );
    } else {
      response = await actions.getOrCreateResponse(
        store,
        sentEventId,
        initialResponse,
      );
    }
    if (initialResponse) {
      if (response) {
        try {
          if (this.props.spamBotPreventionWithDoubleClick) {
            logger.info(
              '[Info] Not auto answering the first module as the flag is set',
            );
          } else {
            // Auto respond only when spambot release flag is not enabled
            await respondToFirstModule(
              store,
              sentEventId,
              response,
              initialResponse,
            );
            //show ux here for bot
            const isPossibleBot = store.survey.getIsPossibleBot();
            if (!isPossibleBot) {
              await proceedAfterFirstModule(
                store,
                sentEventId,
                response,
                () => {
                  this.hasCompletedSurvey = true;
                },
              );
            } else {
              //show bot resolve ux and continue after that
              store.survey.setShowBotDialog(true);
            }
          }
        } catch (error: any) {
          logger.error(error);
          logger.warning('Failed to respond to initial module. Ignoring.');
        }
      } else {
        logger.error(
          `failed to create response for sent event: ${sentEventId}`,
        );
      }
    }
    actions.setClientFetchDone(store);
  }

  // @ts-expect-error - TS4114 - This member must have an 'override' modifier because it overrides a member in the base class 'Component<{ store: IndexStore; survey: Event; progress: number; responder: AudienceMember; sentEvent: SentEvent; currentResponse: SurveyResponse; currentError: SurveyError | null | undefined; params: Record<any, any>; ... 5 more ...; spamBotPreventionWithDoubleClick: boolean; }, {}, any>'. | TS7006 - Parameter 'prevProps' implicitly has an 'any' type.
  componentDidUpdate(prevProps) {
    const {currentQuestion} = this.props;
    if (prevProps.currentQuestion !== currentQuestion) {
      this.track();
    }

    // TODO(marcos): this is a bit clumsy (note, this is the _third_ spot that surveys
    // can be marked as 'completed') but this time we're completing it before responding
    // to the question because a last message module is interpreted as 'the last thing'
    // so we want to complete it when we load the final module.
    // A better solution is to be able to parse the whole module sequence tree and have
    // terminal question nodes 'complete' the survey (or do it on the server) but
    // we don't have an easy way of determining that right now.
    if (
      this.props.progress === this.props.moduleSequence.length - 1 &&
      this.props.currentQuestion?.type === 'message_survey_module'
    ) {
      if (!this.hasCompletedSurvey) {
        actions.finish(
          this.props.store,
          this.props.sentEvent.eventId,
          this.props.sentEvent.id,
        );
        this.hasCompletedSurvey = true;
      }
    }
  }

  track = () => {
    const {store, sentEvent, currentQuestion} = this.props;
    if (currentQuestion) {
      actions.track(store, sentEvent.id, currentQuestion);
    }
  };

  _respondAndAdvance = async (value: any): Promise<void> => {
    const {
      store,
      params: {sentEventId},
    } = this.props;

    const eventId = store.sentEvents.byId(sentEventId).eventId;
    const position = this.props.progress;
    const question = this.props.currentQuestion;
    if (!question) {
      logger.warn(
        `how did respond get called without a question? (@ pos: ${position})`,
      );
      return;
    }

    const advance = () => {
      if (
        !this.hasCompletedSurvey &&
        position === this.props.moduleSequence.length - 1
      ) {
        actions.finish(
          this.props.store,
          this.props.sentEvent.eventId,
          this.props.sentEvent.id,
        );
      }
      actions.goForward(this.props.store, this.props.sentEvent.eventId);
    };

    // Skipping while advancing if the module is of message type (sms/email)
    if (
      question.type === 'message_survey_module' ||
      question.type === 'sms_message_survey_module'
    ) {
      return advance();
    }

    if (
      position === store.survey.firstQuestionPosition(eventId) &&
      this.props.spamBotPreventionWithDoubleClick
    ) {
      // Checking if first question and the flag is enabled
      const query = Object.fromEntries(
        new URLSearchParams(window.location.search).entries(),
      );

      let response;
      if (
        store.survey.getIsSurveyAnswered() &&
        (!query.module_value || !query.module_id)
      ) {
        logger.info(
          '[Info] Not making a GET /responses/sent call because the query is null. While landing on the page, GET call was already made',
        );
        // Get the existing response from store by eventId
        response = store.surveyResponses.byEventId(eventId);
      } else {
        // Always call getOrCreateResponse to ensure we have the real response from the backend
        // before removing the dummy response and making the respond call
        query.module_value = value; // Updating the module_value to the currently selected value in the UI
        const initialResponse = getInitialResponse(query);
        response = await actions.getOrCreateResponse(
          store,
          sentEventId,
          initialResponse,
        );
        if (!response) {
          logger.error(
            `failed to create response for sent event: ${sentEventId}`,
          );
          return; // Exit early if response creation failed
        }
      }

      store.surveyResponses.removeDummyResponse(); // Removing dummyResponse from SurveyResponses which was created while making POST call to responses/initial-response

      // Ensure we have a valid response before proceeding
      if (!response || !response.id) {
        return;
      }

      // @ts-expect-error - TS2322 - Type 'Question | null | undefined' is not assignable to type 'Question'.
      const firstSurveyQuestion: Question = store.survey.firstQuestion(eventId);
      if (firstSurveyQuestion.id !== question.id) {
        // [Safety Check]
        logger.error(
          'Something is wrong. FirstSurveryQuestionId and CurrentQuestionId did not match',
        );
      }
      actions
        .respond(store, response.id, sentEventId, question.id, question, value)
        .then(() => {
          advance();
        })
        .catch((error) => {
          logger.log(`Error saving response: ${error}`);
        });

      actions.setClientFetchDone(store);
      return;
    }

    if (this.props.currentResponse && this.props.currentResponse.id) {
      const requestKey = actions.respond.KEY(
        this.props.currentResponse.id,
        null,
        question.id,
      );
      const inProgress = this.props.store.request.inProgress(requestKey);

      if (inProgress) {
        //logger.info();
        return;
      }

      actions
        .respond(
          this.props.store,
          this.props.currentResponse.id,
          this.props.sentEvent.id,
          question.id,
          question,
          value,
        )
        .then(() => {
          advance();
        })
        .catch((error) => {
          logger.log(`Error saving response: ${error}`);
        });
    } else {
      logger.log('no current response? was it not initialized earlier?');
    }
  };

  acceptBotMessage = async (): Promise<void> => {
    const {
      store,
      params: {sentEventId},
      sentEvent,
    } = this.props;
    const {eventId} = sentEvent;
    const surveyResponse = store.surveyResponses.byEventId(eventId);
    if (surveyResponse) {
      await actions.resolveNonBotUser(store, surveyResponse.id, sentEventId);
    }
    await proceedAfterFirstModule(store, sentEventId, surveyResponse, () => {
      this.hasCompletedSurvey = true;
    });
    store.survey.setShowBotDialog(false);
  };

  // @ts-expect-error - TS4114 - This member must have an 'override' modifier because it overrides a member in the base class 'Component<{ store: IndexStore; survey: Event; progress: number; responder: AudienceMember; sentEvent: SentEvent; currentResponse: SurveyResponse; currentError: SurveyError | null | undefined; params: Record<any, any>; ... 5 more ...; spamBotPreventionWithDoubleClick: boolean; }, {}, any>'.
  render() {
    const {
      responder,
      progress,
      sentEvent,
      currentResponse,
      currentError,
      store,
      moduleSequence,
      agencyDisplayName,
      multiEntityEnabled,
      params,
      currentQuestion,
    } = this.props;
    const entityType = sentEvent.anchor_entity_type;
    const brandColor = get(sentEvent, 'brandingSettings.color', colors.dsCss);
    const buttonColor = get(
      sentEvent,
      'brandingSettings.buttonColor',
      brandColor,
    );
    const questionText = get(currentQuestion, 'question');
    const ogImage = get(sentEvent, 'brandingSettings.ogImage');
    const agencyLogo = get(sentEvent, 'brandingSettings.logo');
    const agencyFavicon = get(sentEvent, 'brandingSettings.favicon');
    //show ux when backend says its a bot
    if (store.survey.getShowBotDialog()) {
      return (
        <Dialog>
          <BotDialog onOk={this.acceptBotMessage} />
        </Dialog>
      );
    }
    // demo agency returning undefined instead of null breaking demos
    if (!isNil(sentEvent.expiredAt)) {
      return (
        <>
          <HeadSetter
            title={agencyDisplayName}
            faviconUrl={agencyFavicon}
            ogImageUrl={ogImage || agencyLogo}
            ogDescription={questionText}
          />
          <ExpiredPage color={brandColor} expiredAt={sentEvent.expiredAt} />
        </>
      );
    }

    if (!store.survey.state.isClientFetchDone) {
      return (
        <SurveyPageLoader
          agencyDisplayName={agencyDisplayName}
          agencyFavicon={agencyFavicon}
          ogImage={ogImage}
          // @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string'.
          questionText={questionText}
        />
      );
    }

    if (progress === moduleSequence.length) {
      return (
        <>
          <HeadSetter
            title={agencyDisplayName}
            faviconUrl={agencyFavicon}
            ogImageUrl={ogImage || agencyLogo}
            ogDescription={questionText}
          />
          <DonePage color={brandColor} />
        </>
      );
    }

    if (store.survey.state.fetching || !currentQuestion) {
      return (
        <SurveyPageLoader
          agencyDisplayName={agencyDisplayName}
          agencyFavicon={agencyFavicon}
          ogImage={ogImage}
          // @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string'.
          questionText={questionText}
        />
      );
    }

    const {eventId} = sentEvent;
    const nextRequiredQuestionIndex = actions.getNextRequiredQuestionIndex(
      eventId,
      store,
      moduleSequence,
      progress,
    );

    return (
      <>
        <HeadSetter
          title={agencyDisplayName}
          faviconUrl={agencyFavicon}
          ogImageUrl={ogImage}
          ogDescription={questionText}
        />
        <DumbSurvey
          sentEventId={params.sentEventId}
          progress={progress}
          userName={responder ? responder.fname : ''}
          // @ts-expect-error - TS2322 - Type 'string | null | undefined' is not assignable to type 'string | undefined'.
          agencyName={agencyDisplayName}
          currentResponse={currentResponse}
          handlePrev={partial(actions.goBack, store, eventId)}
          handleNext={partial(actions.goForward, store, eventId)}
          handleJump={partial(actions.goto, store, eventId)}
          handleResponse={this.respondAndAdvance}
          brandColor={brandColor}
          buttonColor={buttonColor}
          moduleSequence={moduleSequence}
          currentQuestion={currentQuestion}
          // @ts-expect-error - TS2322 - Type 'SurveyError | null | undefined' is not assignable to type 'SurveyError'.
          currentError={currentError}
          nextRequiredQuestionIndex={nextRequiredQuestionIndex}
          multiEntityEnabled={multiEntityEnabled}
          entityType={entityType}
          surveyAnswered={store.survey.getIsSurveyAnswered()}
        />
      </>
    );
  }
}

const mapStateToProps = (state: any) => ({
  agencyDisplayName: get(state, 'agency.agency.brandingSettings.displayName'),
  multiEntityEnabled: state.agency.config.multiEntityEnabled,
});

export default flow(
  withFlux(mapper),
  connect(mapStateToProps, undefined, undefined, {pure: false}),
)(Survey) as any;

// NOTE (kyle): This is basically legacy code. It should probably just be in
// the mapper, but I wanted to factor out the state memoization, and this
// seemed like the simplest option.
// @ts-expect-error - TS7031 - Binding element 'survey' implicitly has an 'any' type. | TS7031 - Binding element 'progress' implicitly has an 'any' type. | TS7031 - Binding element 'moduleSequence' implicitly has an 'any' type.
function getCurrentQuestion(store: any, {survey, progress, moduleSequence}) {
  let currentQuestion;
  if (moduleSequence && moduleSequence[progress]) {
    const currentModule = moduleSequence[progress];
    if (currentModule.parentModuleId) {
      const branchQuestions = store.branches.getBranch(
        currentModule.branchId,
      ).questions;
      currentQuestion = branchQuestions[currentModule.branchModuleIndex];
    } else {
      currentQuestion = find(survey.filteredQuestions, {
        id: currentModule.moduleId,
      });
    }
  }
  return currentQuestion;
}
